import { FC, ReactNode } from 'react';
import { Tooltip } from 'react-tooltip';

import { Document, Padding } from 'types';

import { ReplaceDocumentTagsSources } from 'modules/documents/api/replaceDocumentTags';

import DownloadDocument from './components/DownloadDocument';
import DocumentSpinner from './components/DocumentsSpinner';
import DocumentsOptionsList from './components/DocumentsOptionsList';

type DocumentsColumnProps = {
  className?: string;
  documents: Document[];
  documentsCount: number;
  downloadName?: string;
  hideOnMobile?: boolean;
  index: number;
  isLandscape?: boolean;
  sources?: ReplaceDocumentTagsSources;
  tableView?: boolean;
  templateWatermarkText?: string;
  templateBackgroundImage?: string;
  templateTextPadding?: Padding;
  tooltipId?: string;
  tooltipText?: string | ReactNode;
  tooltipPlace?: 'top' | 'bottom' | 'left' | 'right';
};

const DocumentsColumn: FC<DocumentsColumnProps> = ({
  documents,
  downloadName,
  documentsCount,
  hideOnMobile = true,
  index,
  isLandscape = false,
  sources,
  tableView = true,
  templateWatermarkText = '',
  templateBackgroundImage = '',
  templateTextPadding,
  tooltipId,
  tooltipText = '',
  tooltipPlace = 'bottom',
}) => {
  const className = tableView
    ? `nk-tb-col ${hideOnMobile ? 'tb-col-xl' : ''} text-center align-middle`
    : `nk-file-${hideOnMobile ? 'members' : 'meta'} align-middle`;

  const showLoadedDocuments = () => {
    if (documents?.length === 1) {
      return (
        <DownloadDocument
          document={documents[0]}
          downloadName={downloadName}
          sources={sources}
          templateWatermarkText={templateWatermarkText}
          templateBackgroundImage={templateBackgroundImage}
          templateTextPadding={templateTextPadding}
          isLandscape={isLandscape}
        />
      );
    }
    if (documentsCount > 1) {
      if (documents.length === 1) {
        return <DocumentSpinner />;
      }
      return <DocumentsOptionsList documents={documents} />;
    }
    return <span>-</span>;
  };

  const content =
    documentsCount > 0 ? (
      showLoadedDocuments()
    ) : (
      <span className="btn btn-icon disabled">
        <em className="icon ni ni-file-download" />
      </span>
    );

  return tableView ? (
    <>
      <td className={className} key={`document-column-${index}`}>
        {tooltipText && (
          <Tooltip
            id={`document-column-tooltip-${tooltipId}`}
            place={tooltipPlace}
            style={{ zIndex: 999 }}
          >
            {tooltipText}
          </Tooltip>
        )}
        <div data-tooltip-id={`document-column-tooltip-${tooltipId}`}>
          {content}
        </div>
      </td>
    </>
  ) : (
    <>
      <div className={className} key={`document-div-${index}`}>
        {tooltipText && (
          <Tooltip
            id={`document-div-tooltip-${tooltipId}`}
            place={tooltipPlace}
            style={{ zIndex: 999 }}
          >
            {tooltipText}
          </Tooltip>
        )}
        <div data-tooltip-id={`document-div-tooltip-${tooltipId}`}>
          {content}
        </div>
      </div>
    </>
  );
};

export default DocumentsColumn;
